import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { FastField, Field, Form, Formik } from 'formik';
import _ from 'lodash';

import lineEndpoint from 'app/api/services/lineEndpoint';
import notification from 'app/config/notification';
import { URLS } from 'app/config/urls';
import { errorHandler } from 'app/utils/errorHandler';

import { formataDescricao, onSelectMunicipio } from '../form-configs/line-form-helpers';

import { clearSelectedLine, fetchGrants, fetchLine, fetchServices, setParentLine } from 'app/store/slices/line';
import { clearOrgaos, fetchDefaultOrgao, fetchOrgaos, fetchPolos } from 'app/store/slices/forms';
import { fetchSubSystems, fetchVehicleClassification, fetchVehicleTypes } from 'app/store/slices/vehicle';
import { validateFormikForm } from '../../../utils/forms/functions';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';

import Loading from 'app/layouts/Loading';
import ConfirmDialog from 'app/components/ConfirmDialog';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import CustomTextField from '../components/TextField';
import FieldsLinesStrip from '../components/FieldsLinesStrip';
import FieldsLines from '../components/FieldsLines';

import { lineInitialValues, lineStripInitialValues } from '../form-configs/line-initial-values';
import { manageLineStripValidationSchema, manageLineValidationSchema } from '../form-configs/validation-schemas';
import { generatePath } from 'react-router-dom';
import LineActivateModal from './modals/LineActivateModal';
import CustomMaskField from '../components/MaskField';

const LinesList = () => {
    const match = useRouteMatch();
    const history = useHistory();
    const dispatch = useDispatch();
    const screenIsMounted = React.useRef();

    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [createdLine, setCreatedLine] = useState(null);
    const [deDescricao, setDeDescricao] = useState('');

    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [openActivateModal, setOpenActivateModal] = useState(false);
    const [formInitials, setFormInitials] = useState(lineInitialValues);

    const polos = useSelector((state) => state.forms.polos);
    const orgaos = useSelector((state) => state.forms.orgaos);
    const defaultOrgao = useSelector((state) => state.forms.defaultOrgao);
    const subSystems = useSelector((state) => state.vehicle.subSystems);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const vehicleClassification = useSelector((state) => state.vehicle.vehicleClassification);

    const lineGrants = useSelector((state) => state.line.grants);
    const lineServices = useSelector((state) => state.line.services);
    const selectedLine = useSelector((state) => state.line.selected);
    const parentLine = useSelector((state) => state.line.parentLine);
    const isStrip = useSelector((state) => state.line.isStrip);

    const hasPathParam = Boolean(match.params.cdLinha);

    const uf = 'BA';

    useEffect(() => {
        (async () => {
            setLoading(true);
            const promisses = [];
            if (vehicleType.length === 0) promisses.push(dispatch(fetchVehicleTypes()));
            if (vehicleClassification.length === 0) promisses.push(dispatch(fetchVehicleClassification()));
            if (subSystems.length === 0) promisses.push(dispatch(fetchSubSystems()));
            if (lineGrants.length === 0) promisses.push(dispatch(fetchGrants()));
            if (lineServices.length === 0) promisses.push(dispatch(fetchServices()));
            if (polos.length === 0) promisses.push(dispatch(fetchPolos(uf)));
            if (isStrip && orgaos.length === 0) promisses.push(dispatch(fetchOrgaos()));
            if (isStrip && Object.keys(defaultOrgao).length === 0) promisses.push(dispatch(fetchDefaultOrgao()));
            if (promisses.length > 0) await Promise.all(promisses);
            setLoading(false);
        })();
        return () => {
            dispatch(clearOrgaos());
            dispatch(clearSelectedLine());
            dispatch(setParentLine(null));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        screenIsMounted.current = true;
        if (hasPathParam) {
            (async () => {
                try {
                    setLoading(true);
                    if (!selectedLine) await dispatch(fetchLine(match.params.cdLinha));
                    if (screenIsMounted.current && selectedLine) {
                        setEditMode(true);
                        let tempLineData = Object.assign({}, selectedLine);
                        if (!selectedLine.deRazaoSocial) {
                            tempLineData.deRazaoSocial = selectedLine.empresa?.deRazaoSocial;
                        }
                        setFormInitials(tempLineData);
                        setDeDescricao(selectedLine.deDescricao);
                    } else {
                        const tempInitialValue = isStrip ? lineStripInitialValues : lineInitialValues;
                        tempInitialValue.cdOrgaoTermoContrato = defaultOrgao?.cdOrgao;
                        setFormInitials(tempInitialValue);
                    }
                } catch (error) {
                    errorHandler(error.response?.status, error.response?.data?.message);
                } finally {
                    setLoading(false);
                }
            })();
        } else {
            const tempInitialValue = isStrip ? lineStripInitialValues : lineInitialValues;
            tempInitialValue.cdOrgaoTermoContrato = defaultOrgao?.cdOrgao;
            setFormInitials(tempInitialValue);
        }

        return () => {
            setEditMode(false);
            screenIsMounted.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isStrip, selectedLine, match.params, defaultOrgao]);

    const handleCancel = () => {
        dispatch(clearSelectedLine());
        const params = {};
        if (isStrip) params.isStrip = 'strip';
        history.push(generatePath(URLS.LINES_MANAGE, params));
    };

    const renderActivateLabel = React.useCallback(() => {
        if (editMode) {
            return selectedLine?.boAtivo ? 'Inativar' : 'Ativar';
        }
    }, [editMode, selectedLine]);

    const handleSubmitForm = async (values) => {
        try {
            setLoading(true);
            const valuesCopy = { ...values };
            if (parentLine) {
                valuesCopy.cdLinhaPai = parentLine.cdLinha;
            }
            const savedLine = await lineEndpoint.saveLine(valuesCopy);
            if (savedLine.status === 200) {
                notification('Linha salva com sucesso', {
                    toastId: 'SUCCESSFUL_SAVE_REQUEST',
                    type: 'success',
                });
            }

            if (editMode) {
                setLoading(false);
                // history.push(URLS.LINES);
            } else {
                setCreatedLine(savedLine.data);
                setOpenConfirmModal(true);
            }
            dispatch(clearSelectedLine());
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
            setLoading(false);
        }
    };

    function renderConfirmDialog() {
        return (
            <ConfirmDialog
                open={openConfirmModal}
                onClose={() => setOpenConfirmModal(false)}
                handleClickNo={() => {
                    setOpenConfirmModal(false);
                    const params = { cdLinha: createdLine.cdLinha };
                    if (isStrip) params.isStrip = 'strip';
                    history.push(generatePath(URLS.LINES_MANAGE_EDIT, params));
                }}
                handleClickYes={() => {
                    setOpenConfirmModal(false);
                    const params = { cdLinha: createdLine.cdLinha };
                    if (isStrip) params.isStrip = 'strip';
                    history.push(generatePath(URLS.LINES_MANAGE_ITINERARIES_NEW, params));
                }}
                text="Linha salva com sucesso. Deseja iniciar o cadastro de itinerários?"
            />
        );
    }

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={formInitials}
                validationSchema={isStrip ? manageLineStripValidationSchema : manageLineValidationSchema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmitForm}>
                {(formProps) => {
                    const { values, handleSubmit, handleChange, setFieldValue } = formProps;
                    return (
                        <Form>
                            <PageWrapper
                                title={`Cadastro - Edição de ${isStrip ? 'linha/serviço' : 'linha'}`}
                                actionButton={
                                    <FormControlLabel
                                        label="INATIVADA"
                                        control={
                                            <Checkbox
                                                checked={!values.boAtivo}
                                                disabled
                                                name="boAtivo"
                                                color="primary"
                                            />
                                        }
                                    />
                                }>
                                {loading && <Loading />}
                                {createdLine && renderConfirmDialog()}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={4}>
                                            <Field
                                                component={CustomTextField}
                                                label="Número do processo SEI"
                                                name="nuProcessoSei"
                                                inputProps={{
                                                    maxLength: 30,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Field
                                                component={CustomMaskField}
                                                name="nuProcesso"
                                                label="Número do Processo"
                                                mask={'9999.9999/999999'}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <FastField
                                            component={CustomTextField}
                                            label="Número"
                                            name="nuLinha"
                                            onChange={(e) => {
                                                const temp = _.cloneDeep(e);
                                                handleChange(temp);
                                                const tempDeDescricao = formataDescricao({
                                                    ...values,
                                                    nuLinha: e.target.value,
                                                });
                                                setDeDescricao(tempDeDescricao);
                                                setFieldValue('deDescricao', tempDeDescricao);
                                            }}
                                            inputProps={{
                                                maxLength: 30,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Field
                                            component={CustomTextField}
                                            label="Origem"
                                            name="deOrigem"
                                            onChange={(e) => {
                                                handleChange(e);
                                                const tempDeDescricao = formataDescricao({
                                                    ...values,
                                                    deOrigem: e.target.value,
                                                });
                                                setDeDescricao(tempDeDescricao);
                                                setFieldValue('deDescricao', tempDeDescricao);
                                            }}
                                            inputProps={{
                                                maxLength: 200,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Field
                                            component={CustomTextField}
                                            label="Destino"
                                            name="deDestino"
                                            onChange={(e) => {
                                                handleChange(e);
                                                const tempDeDescricao = formataDescricao({
                                                    ...values,
                                                    deDestino: e.target.value,
                                                });
                                                setDeDescricao(tempDeDescricao);
                                                setFieldValue('deDescricao', tempDeDescricao);
                                            }}
                                            inputProps={{
                                                maxLength: 200,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            component={CustomTextField}
                                            label={isStrip ? 'Linha/Serviço' : 'Descrição da linha'}
                                            name="deDescricao"
                                            value={deDescricao}
                                            onChange={(e) => {
                                                handleChange(e);
                                                setDeDescricao(e.target.value);
                                            }}
                                            inputProps={{
                                                maxLength: 500,
                                            }}
                                            // disabled
                                        />
                                    </Grid>
                                    {!isStrip && (
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                component={CustomTextField}
                                                label="Polo"
                                                name="cdMunicipioPolo"
                                                select
                                                loading={loading}
                                                onChange={(event) =>
                                                    onSelectMunicipio({
                                                        event,
                                                        cityObj: 'polo',
                                                        setFieldValue,
                                                        polos,
                                                    })
                                                }
                                                SelectProps={{
                                                    native: true,
                                                }}>
                                                <option aria-label="None" value="">
                                                    Selecione
                                                </option>
                                                {polos?.map((polo, index) => {
                                                    return (
                                                        <option key={`polo-${index}`} value={polo.cdMunicipio}>
                                                            {polo.nmMunicipio}
                                                        </option>
                                                    );
                                                })}
                                            </Field>
                                        </Grid>
                                    )}

                                    {!isStrip ? (
                                        <FieldsLines formikBag={formProps} loading={loading} />
                                    ) : (
                                        <FieldsLinesStrip formikBag={formProps} loading={loading} />
                                    )}

                                    <Grid item xs={12}>
                                        <FastField
                                            component={CustomTextField}
                                            label="Observação"
                                            name="deObservacao"
                                            multiline
                                            minRows={4}
                                            inputProps={{
                                                maxLength: 2000,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        onSave={() => {
                                            validateFormikForm(
                                                formProps,
                                                isStrip ? manageLineStripValidationSchema : manageLineValidationSchema,
                                            ).then((res) => {
                                                if (Object.keys(res).length > 0) {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                } else {
                                                    handleSubmit(values);
                                                }
                                            });
                                        }}
                                        onCancel={handleCancel}
                                        lastButtonLabel={editMode ? renderActivateLabel() : null}
                                        onClickLastButton={() => (editMode ? setOpenActivateModal(true) : null)}
                                        firstButtonLabel={'Voltar'}
                                    />
                                </Grid>
                            </PageWrapper>
                        </Form>
                    );
                }}
            </Formik>
            <LineActivateModal
                open={openActivateModal}
                onClose={() => setOpenActivateModal(false)}
                title={renderActivateLabel()}
                selectedLine={selectedLine}
            />
        </>
    );
};

export default LinesList;
